import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import {
    addAttributeTypeSelection,
    getSelectedAttributeTypes,
    AttributeTypeState,
    getAttributeTypes,
    getAttributeTypeState,
    clearFilters,
    updateCheckboxes,
    getNextPageAttributeTypes,
    getCheckboxState,
    searchAttributeTypes,
    setCheckboxes,
    getCountries,
} from '@emi/shared/state/filters';
import { Paging } from '@emi/shared/constants';
import { AttributeType, CheckboxTreeNode, Page } from '@emi/shared/models';
import { select, Store } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import * as Constants from '@emi/shared/constants';

@Component({
    selector: 'via-filter-attribute-types',
    templateUrl: './filter-attribute-types.component.html',
    styleUrls: ['./filter-attribute-types.component.scss'],
})
export class FilterAttributeTypesComponent implements OnInit, OnDestroy {
    @Input()
    public applyClicked: EventEmitter<boolean>;

    @Input()
    public cancelClicked: EventEmitter<boolean>;

    @Input()
    public showApplyButton: boolean;

    @Input()
    public isOverlay: boolean;

    @Output()
    public showApplyButtonChange = new EventEmitter<boolean>();

    @ViewChild('scrollArea')
    public scrollArea: ElementRef;

    @ViewChildren('page')
    public pageElements: QueryList<ElementRef>;

    @ViewChildren('pageAnchor')
    public pageAnchorElements: QueryList<ElementRef>;

    private attributeTypesSubscription: Subscription;
    private applyClickedSubscription: Subscription;
    private cancelClickedSubscription: Subscription;
    private checkBoxSubscription: Subscription;
    private stateSubscription: Subscription;
    public attributeTypesTree: Array<CheckboxTreeNode> = [];
    public selectedAttributeTypes: Array<AttributeType> = [];
    public stateAttributeTypes: Array<AttributeType> = [];
    public loading: boolean;
    public showError = false;
    public checkedArray: Array<CheckboxTreeNode> = [];
    public errorMessage = '';
    public pages: Page[] = [];
    public totalSize: number | undefined;
    public eventsSubject: Subject<CheckboxTreeNode[]> = new Subject<CheckboxTreeNode[]>();
    private nextPageId: string | undefined;
    public hasSearchedByText = false;
    public noResultsMessage = '';
    private isLargeViewport: boolean;

    constructor(private readonly store: Store<AttributeTypeState>, private changeDetector : ChangeDetectorRef) {}

    public onCheckboxStateChange(selectedCheckbox: CheckboxTreeNode): void {
        this.store.dispatch(updateCheckboxes({ checkbox: selectedCheckbox }));
    }

    public onScrollDown(): void {
        if (this.nextPageId) {
            this.store.dispatch(getNextPageAttributeTypes());
        }
    }

    public ngOnInit(): void {
        this.checkBoxSubscription = this.store.pipe(select(getCheckboxState)).subscribe((y) => {
            this.checkedArray = y.checkboxes;
        });

        this.attributeTypesSubscription = this.store.pipe(select(getSelectedAttributeTypes)).subscribe((attributeTypes) => {
            if (attributeTypes.length > 0) {
                if (this.checkedArray.length === 0) {
                    this.store.dispatch(
                        setCheckboxes({
                            checkboxes: attributeTypes.map((attributeType) => {
                                return this.buildCheckboxTreeNode(attributeType, true);
                            }),
                        })
                    );
                }
                this.selectedAttributeTypes = attributeTypes;
                this.attributeTypesTree = attributeTypes.map((attributeType) => {
                    return this.buildCheckboxTreeNode(attributeType);
                });
                let nextPage = new Page('', this.attributeTypesTree);
                this.pages.push(nextPage);
                this.eventsSubject.next(nextPage.value);
            }
            this.checkForResults();
        });

        this.stateSubscription = this.store.pipe(select(getAttributeTypeState)).subscribe((state) => {
            this.loading = state.loading;
            this.showError = state.failed;
            this.errorMessage = state.error;
            this.nextPageId = state.metadata?.nextPageId;
            this.totalSize = state.metadata?.totalResults;
            this.pages = [];
            if (this.selectedAttributeTypes.length === 0) {
                this.attributeTypesTree = state.attributeTypes.map((attributeType) => {
                    return this.buildCheckboxTreeNode(attributeType);
                });
            }
            this.eventsSubject.next(this.attributeTypesTree);
            while (this.attributeTypesTree.length > 0) {
                let newPage = this.attributeTypesTree.splice(0, Paging.PAGE_SIZE);
                this.pages.push(new Page(this.nextPageId, newPage));
            }
            this.stateAttributeTypes = state.attributeTypes;
            this.checkForResults();

            if (!this.isLargeViewport || this.pages.length > Constants.Paging.LARGE_VIEWPORT_PAGE_THRESHOLD) {
                this.scrollToLastPage();
            }
        });

        this.applyClickedSubscription = this.applyClicked.subscribe(() => {
            const attributeTypes: Array<AttributeType> = this.checkedArray
                .map((attributeType) => new AttributeType(attributeType.id, attributeType.label))
                .sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1));
            this.store.dispatch(addAttributeTypeSelection({ attributeTypes }));
            this.store.dispatch(getCountries());
            this.store.dispatch(clearFilters());
        });

        if (this.isOverlay) {
            this.cancelClickedSubscription = this.cancelClicked.subscribe(() => {
                this.pages = [];
                this.store.dispatch(
                    setCheckboxes({
                        checkboxes: this.selectedAttributeTypes.map((attributeType) => {
                            return this.buildCheckboxTreeNode(attributeType, true);
                        }),
                    })
                );
                const currentSelectableAttributeTypes =
                    this.selectedAttributeTypes.length > 0 ? this.selectedAttributeTypes : this.stateAttributeTypes;
                this.attributeTypesTree = currentSelectableAttributeTypes.map((attributeType) => {
                    return this.buildCheckboxTreeNode(attributeType, this.selectedAttributeTypes.length > 0);
                });
                this.eventsSubject.next(this.attributeTypesTree);
                while (this.attributeTypesTree.length > 0) {
                    let newPage = this.attributeTypesTree.splice(0, Paging.PAGE_SIZE);
                    this.pages.push(new Page(this.nextPageId, newPage));
                }
            });
        }

        if (this.selectedAttributeTypes.length === 0 && this.stateAttributeTypes.length === 0) {
            this.store.dispatch(getAttributeTypes());
        }
    }

    public buildCheckboxTreeNode(attributeType: AttributeType, completed?: boolean): CheckboxTreeNode {
        if (completed) {
            return new CheckboxTreeNode(attributeType.id, attributeType.name, 'checked');
        }
        const existingAttributeType = this.checkedArray.find((x) => x.id === attributeType.id);
        return new CheckboxTreeNode(attributeType.id, attributeType.name, existingAttributeType ? 'checked' : 'unchecked');
    }

    public searchFiltersByText(text: string): void {
        this.hasSearchedByText = true;
        this.store.dispatch(searchAttributeTypes({ searchText: text, delay: Constants.GlobalVariables.FILTER_SEARCH_DELAY }));
    }

    public resetSearch(): void {
        this.hasSearchedByText = false;
        this.store.dispatch(searchAttributeTypes({ searchText: '', delay: 0 }));
    }

    public selectionChanged(): void {
        this.showApplyButtonChange.emit(this.selectedAttributeTypes.length !== this.checkedArray.length && this.checkedArray.length > 0);
    }

    public checkForResults(): void {
        if (!this.loading && this.stateAttributeTypes.length == 0 && !this.hasSearchedByText && this.selectedAttributeTypes.length == 0) {
            this.noResultsMessage = Constants.GlobalVariables.ZERO_SKUS;
            return;
        }
        if (!this.loading && this.stateAttributeTypes.length == 0 && this.hasSearchedByText && this.selectedAttributeTypes.length == 0) {
            this.noResultsMessage = Constants.GlobalVariables.NO_SEARCH_RESULTS;
            return;
        }
        this.noResultsMessage = '';
    }

    public scrollToLastPage(): void {
        if (this.pageAnchorElements && this.pageAnchorElements.last && this.pageAnchorElements.last.nativeElement) {
            // TODO: investigate alternative to setTimeout
            setTimeout(() => {
                if (!this.isLargeViewport) {
                    this.pageAnchorElements.last.nativeElement.scrollIntoView();
                } else {
                    const pageAnchorIndex = this.pageAnchorElements.length - Constants.Paging.LARGE_VIEWPORT_PAGE_THRESHOLD;
                    this.pageAnchorElements.get(pageAnchorIndex)?.nativeElement.scrollIntoView();
                }
            }, 0);
        }
    }

    public ngOnDestroy(): void {
        if (this.hasSearchedByText && this.selectedAttributeTypes.length === 0) {
            this.store.dispatch(searchAttributeTypes({ searchText: '', delay: 0 }));
        }
        this.checkBoxSubscription.unsubscribe();
        this.stateSubscription.unsubscribe();
        this.attributeTypesSubscription.unsubscribe();
        this.applyClickedSubscription.unsubscribe();
        if (this.isOverlay) {
            this.cancelClickedSubscription.unsubscribe();
        }
        this.showApplyButtonChange.emit(false);
    }

    public checkScrollArea(): void {
        this.changeDetector.detectChanges();

        const pageHeight = this.pageElements.first.nativeElement.clientHeight;
        const scrollAreaHeight = this.scrollArea.nativeElement.clientHeight;
        const scrollDistance = this.scrollArea.nativeElement.scrollHeight;

        if (pageHeight <= scrollAreaHeight && this.totalSize && this.totalSize > Paging.PAGE_SIZE) {
            this.isLargeViewport = true;

            if (scrollDistance <= scrollAreaHeight) { // Load another page only if there is no scrollbar
                this.onScrollDown();
            }
        }
    }
}
